import { z } from 'zod';
import { agentChangesSchema } from './agent-change-schemas';
import { agentCheckIssueSchema, agentIdSchema, optionalAgentIdSchema } from './agent-tool-schemas';
// Tools for a claim intake agent's form: the blocks behind its Gather Claim Info step, each with its fields, its
// Instruction and the condition that decides whether a call is asked it. The browser runs them against the open
// agent's draft; the evals run the same logic against fixtures.
const SUB_FIELD_TYPES = [
    'text',
    'textarea',
    'number',
    'boolean',
    'options',
    'email',
    'phone',
    'phoneWithExtension',
    'ssn',
    'simpleDate',
    'date',
    'simpleTime',
    'datetime',
    'address'
];
export const FORM_FIELD_TYPES = [...SUB_FIELD_TYPES, 'list', 'object'];
const fieldTypeSchema = z
    .enum(FORM_FIELD_TYPES)
    .describe('text, textarea (long text), number, boolean (Yes/no), options (fixed choices), email, phone, phoneWithExtension, ssn (Social Security Number), simpleDate (YYYY-MM-DD), date (with time zone), simpleTime, datetime (with time zone), address (US and Canada), list (several of something), object (a group of sub-fields)');
export const FORM_RULE_OPERATIONS = [
    'equals',
    'not_equals',
    'inlist',
    'not_inlist',
    'answered',
    'not_answered'
];
export const formRuleSchema = z.object({
    field: z
        .string()
        .describe("The field whose saved answer the rule reads, by id or name. For a block's condition: a field of an earlier block. For a field's condition: an earlier field of its own block"),
    operation: z
        .enum(FORM_RULE_OPERATIONS)
        .describe('equals and not_equals take `value`, and need a Yes/no, Options or Text field. inlist and not_inlist take `values`, and need an Options or Text field. answered and not_answered take neither, and work on any field'),
    value: z
        .union([z.string(), z.boolean()])
        .optional()
        .describe('true or false for a Yes/no field; one of its options, spelled exactly, for an Options field'),
    values: z
        .array(z.string())
        .optional()
        .describe("The options that match, spelled as the field's options are")
});
export const askWhenSchema = z
    .union([
    z.literal('always'),
    z.object({
        anyOf: z
            .array(z.object({ allOf: z.array(formRuleSchema).min(1) }))
            .min(1)
            .describe('Groups of rules. It is asked when every rule in at least one group matches: one group of one rule for a single condition, one group of several for "and", several groups for "or"')
    })
])
    .describe('always: asked on every call. Otherwise the rules that decide it, read from answers already saved');
const subFieldSchema = z.object({
    name: z.string().min(1),
    type: z.enum(SUB_FIELD_TYPES),
    required: z.boolean().optional(),
    helpText: z.string().optional().describe('What the value is and how to store it'),
    options: z.array(z.string()).optional().describe('The choices, for an options sub-field')
});
const fieldSettingsSchema = z.object({
    name: z
        .string()
        .min(1)
        .describe("The key the answer is saved under, unique in its block. Follow the form's naming, such as lossDate"),
    type: fieldTypeSchema,
    required: z
        .boolean()
        .optional()
        .describe("The agent can't get past the block without it. Every field a condition reads should be required"),
    helpText: z
        .string()
        .optional()
        .describe('What the value is and how to store it, written for the agent, never as a question to the caller. It reaches the agent only when it saves, unless the block has no Instruction'),
    options: z.array(z.string()).optional().describe('The choices, for an options field'),
    askWhen: askWhenSchema
        .optional()
        .describe('A condition on this one field, read from earlier fields of the same block. Defaults to always'),
    subFields: z
        .array(subFieldSchema)
        .optional()
        .describe('For an object: its sub-fields. For a list: the sub-fields of each item; leave it out for a list of plain text values')
});
const blockRefSchema = z
    .string()
    .describe('A block id or title from get_intake_form, or the ref of a block added earlier in this batch');
const fieldRefSchema = z.string().describe("A field's id or name, in that block");
const afterBlockSchema = blockRefSchema
    .optional()
    .describe('The block it goes after. Leave out to put it last when adding, first when moving');
export const intakeFormOperationSchema = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('add_block'),
        ref: z
            .string()
            .describe('A temporary name for the new block, so later operations in this batch can use it'),
        title: z
            .string()
            .min(1)
            .describe("What the block is about, unique in the form. Letters, numbers, spaces and hyphens only, 54 characters at most. It names the block's save and the key its answers are filed under"),
        instruction: z
            .string()
            .optional()
            .describe('How to ask this block: what to ask, in what order, and what to do with the answers'),
        askWhen: askWhenSchema.optional().describe('Defaults to always'),
        fields: z.array(fieldSettingsSchema).describe('The answers the block saves, eight at most'),
        after: afterBlockSchema
    }),
    z.object({
        op: z.literal('update_block'),
        block: blockRefSchema,
        title: z.string().min(1).optional(),
        instruction: z.string().optional().describe('The whole new Instruction; an empty string removes it'),
        askWhen: askWhenSchema.optional()
    }),
    z.object({ op: z.literal('remove_block'), block: blockRefSchema }),
    z.object({ op: z.literal('move_block'), block: blockRefSchema, after: afterBlockSchema }),
    z.object({
        op: z.literal('add_field'),
        block: blockRefSchema,
        field: fieldSettingsSchema,
        after: fieldRefSchema.optional().describe('The field it goes after. Leave out to put it last')
    }),
    z.object({
        op: z.literal('update_field'),
        block: blockRefSchema,
        field: fieldRefSchema,
        changes: fieldSettingsSchema
            .partial()
            .describe("Only what you're changing. `options` and `subFields` are the whole new list. A new name is followed by every condition that reads the field")
    }),
    z.object({ op: z.literal('remove_field'), block: blockRefSchema, field: fieldRefSchema }),
    z.object({
        op: z.literal('move_field'),
        block: blockRefSchema,
        field: fieldRefSchema,
        toBlock: blockRefSchema
            .optional()
            .describe('The block it moves to. Leave out to move it within its block'),
        after: fieldRefSchema.optional().describe('The field it goes after there. Leave out to put it first')
    })
]);
const subFieldViewSchema = z.object({
    name: z.string(),
    type: z.string(),
    required: z.boolean(),
    helpText: z.string().optional(),
    options: z.array(z.string()).optional()
});
const fieldViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    required: z.boolean(),
    helpText: z.string().optional(),
    options: z.array(z.string()).optional(),
    askedOnlyIf: z.string().optional().describe('Its own condition, in words. Missing when it has none'),
    askWhen: askWhenSchema
        .optional()
        .describe('Its own condition as edit_intake_form takes it, when it can be written that way'),
    mustBe: z.string().optional().describe('A range the value has to fall in'),
    subFields: z.array(subFieldViewSchema).optional(),
    locked: z.string().optional().describe("Why this field can't be changed here, when it can't"),
    decides: z
        .array(z.string())
        .optional()
        .describe('The blocks and fields whose conditions read this answer. Changing its type, options or name, making it optional, moving it or removing it changes when they are asked')
});
const blockViewSchema = z.object({
    id: z.string(),
    title: z.string(),
    saveAction: z.string().describe("The save this block makes, as it appears among a conversation's actions"),
    askedWhen: z.string().describe('"Always", or its condition in words'),
    askWhen: askWhenSchema
        .optional()
        .describe('Its condition as edit_intake_form takes it, when it can be written that way'),
    instruction: z
        .string()
        .optional()
        .describe('How the agent is told to ask this block. When set it is all the agent reads about asking, so a field it leaves out is only asked if required'),
    fields: z.array(fieldViewSchema)
});
export const intakeFormViewSchema = z.object({
    agentId: z.string(),
    version: z
        .object({ versionId: z.string(), savedAt: z.string(), publishedAt: z.string().optional() })
        .optional()
        .describe('Set when you read a past version: the form as it was then'),
    canEdit: z.boolean().describe('Whether edit_intake_form can change this form'),
    cannotEditBecause: z.string().optional().describe('Why not, to tell the user'),
    counts: z
        .object({ blocks: z.number(), conditionalBlocks: z.number(), fields: z.number() })
        .describe('Across the whole form. Quote these rather than counting'),
    blocks: z.array(blockViewSchema).describe('In the order a call reaches them'),
    blocksLeftOut: z.number().optional().describe('Blocks not returned because `blocks` named others'),
    outlineOnly: z
        .boolean()
        .optional()
        .describe('The form is too long to return whole, so Instructions and help text were left out. Pass `blocks` to read the ones you need in full'),
    issues: z
        .array(agentCheckIssueSchema)
        .describe("Problems in the form as it is now, the form builder's own included"),
    formatter: z
        .object({
        inputs: z.array(z.string()),
        code: z.string().optional().describe('Left out unless you ask for it')
    })
        .optional()
        .describe('The code that reshapes the saved answers for the Get Claim Data step, when the form has one')
});
const formReadInputSchema = z.object({
    agentId: optionalAgentIdSchema,
    versionId: z
        .string()
        .optional()
        .describe("A version to read instead of the current draft, such as a conversation's agentVersionId"),
    blocks: z
        .array(z.string())
        .optional()
        .describe('Only these blocks, by id or title. Leave out for the whole form')
});
export const getIntakeFormToolSchema = {
    name: 'get_intake_form',
    description: "Read a claim intake agent's form, the blocks behind its Gather Claim Info step, including unsaved edits: every block in order with its title, the condition that decides whether a call is asked it, its Instruction, and each field with its type, whether it's required, its options, help text and own condition, plus which answers other blocks' conditions depend on and any problems in the form. Call it before changing the form, and when working out why a call asked or skipped something. With versionId, read the form as a conversation ran it.",
    inputSchema: formReadInputSchema.extend({
        includeFormatterCode: z.boolean().optional().describe("Also return the formatter's code")
    }),
    outputSchema: intakeFormViewSchema
};
export const showIntakeFormToolSchema = {
    name: 'show_intake_form',
    description: "Show the user a claim intake agent's form as a card: its blocks in order, which are always asked and which depend on an earlier answer, and each block's fields when opened. Use it when the user asks to see the form, its blocks or its conditions. The card holds the detail: reply with what matters about it, never a list of every block and field.",
    inputSchema: formReadInputSchema.extend({
        highlight: z
            .array(z.string())
            .optional()
            .describe('Blocks to open on the card, by id or title, such as the ones the user asked about')
    }),
    outputSchema: intakeFormViewSchema.extend({
        agentName: z.string(),
        highlight: z.array(z.string()).optional().describe('Ids of the blocks the card opens')
    })
};
export const editIntakeFormToolSchema = {
    name: 'edit_intake_form',
    description: "Change a claim intake agent's form, as one batch that's applied all at once or not at all: add, change, move or remove blocks and fields, and set the conditions that decide when each is asked. Call get_intake_form first for ids. One change often needs others to stay correct, such as a new gate question plus the condition that reads it, so put everything one change needs in the same batch; a batch that would leave a condition reading a missing field, a later block or a value that isn't an option is refused with what to fix. Changes land in the agent's unsaved draft, and the user sees what changed, block by block, with Undo.",
    inputSchema: z.object({
        agentId: agentIdSchema,
        summary: z.string().describe('One short sentence describing the change, shown to the user'),
        operations: z.array(intakeFormOperationSchema).min(1)
    }),
    outputSchema: z.object({
        agentId: z.string(),
        editId: z.string(),
        summary: z.string(),
        applied: z.array(z.string()).describe('What each operation did'),
        addedBlockIds: z.record(z.string(), z.string()).describe('Refs of added blocks mapped to their new ids'),
        forCallers: z
            .array(z.string())
            .describe('What the change does to a call: which blocks and fields are now asked, skipped or reached differently. Tell the user anything here they did not ask for'),
        checkAfter: z
            .array(z.string())
            .describe('What outside the form still uses a name this edit changed or removed, such as the formatter, a workflow step or a step of the flow. Fix the ones you can and tell the user about the rest'),
        issues: z
            .array(agentCheckIssueSchema)
            .describe('Problems this edit added to the form, such as a condition that now reads an answer nobody has to give. Fix them or tell the user'),
        // Optional so edits from before it still read
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, block by block, as its card shows it')
    })
};
//# sourceMappingURL=intake-form-tool-schemas.js.map